import { useState, useEffect } from "react";
import { HorseArt } from "@/components/game/HorseArt";
import { StatBar } from "@/components/game/StatBar";
import { ChevronLeft, Warehouse, BookOpen, Trophy, Dumbbell, User, Utensils, Coins, Flag, ShieldCheck, GraduationCap, CalendarCheck, Medal, Award, Lock, Heart, Gem, Sparkles, ArrowRight, Pencil, ShoppingBag, Share2 } from "lucide-react";
import { ReadinessMeter, ReadinessPill } from "@/components/game/ReadinessMeter";
import { BarnHome } from "@/components/game/BarnHome";
import { NaturalStatsPanel, DisciplinePanel, AdvancedTrainingPanel, CompetitionPanel, AwardsPanel, LiveShowsBoard, isEligible } from "@/components/game/HorseCareer";
import { gameApi } from "@/lib/api";
import { toast } from "sonner";
import { TrainingDay } from "@/components/game/TrainingDay";
import { DailyCare } from "@/components/game/DailyCare";
import { HorseTackTab, MakeoverTackTab } from "@/components/game/PerfTack";
import { HorseGenetics } from "@/components/game/HorseGenetics";
import { BackgroundButton } from "@/components/game/HorseBackground";
import { PublicProfile } from "@/components/game/HMAViews";

const ACH_ICONS = { Flag, ShieldCheck, GraduationCap, CalendarCheck, Medal, Award, Warehouse, Coins, Trophy, Heart };

const STAT_KEYS = ["trust", "handling", "manners", "confidence", "conditioning", "trail", "freestyle", "riding", "liberty", "popularity"];
const LABEL = { trust: "Trust", handling: "Handling", manners: "Manners", confidence: "Confidence", conditioning: "Conditioning", trail: "Trail", freestyle: "Freestyle", riding: "Riding", liberty: "Liberty", popularity: "Popularity" };

const Back = ({ onBack, title, year }) => (
  <div className="flex items-center gap-2 px-4 pt-4">
    <button data-testid="view-back" onClick={onBack} className="flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-sm font-semibold text-primary"><ChevronLeft size={15} /> Back</button>
    <h2 className="font-display text-2xl font-bold text-primary">{title}</h2>
    {year != null && <span data-testid="view-game-year" className="ml-auto flex items-center gap-1 rounded-full bg-secondary/15 px-2.5 py-1 text-sm font-bold text-secondary"><CalendarCheck size={14} /> Year {year}</span>}
  </div>
);

const RENAME_COST = 500;
export const RenameControl = ({ endpoint, currentName, onRenamed }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(currentName || "");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const n = name.trim();
    if (n.length < 1 || n.length > 24) { toast.error("Name must be 1–24 characters."); return; }
    setBusy(true);
    try {
      const { data } = await gameApi.post(endpoint, { name: n });
      toast.success(`Renamed to ${data.name} (−${data.cost}🪙)`);
      setOpen(false);
      onRenamed && onRenamed(data.name, data.player);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not rename horse."); }
    finally { setBusy(false); }
  };
  return (
    <>
      <button data-testid="rename-horse-btn" onClick={(e) => { e.stopPropagation(); setName(currentName || ""); setOpen(true); }}
        className="rounded-full p-1 text-muted-foreground hover:text-primary active:scale-90" aria-label="Rename horse"><Pencil size={14} /></button>
      {open && (
        <div className="fixed inset-0 z-[90] grid place-items-center bg-black/60 p-4" data-testid="rename-modal" onClick={() => setOpen(false)}>
          <div className="w-full max-w-sm rounded-2xl bg-card p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <p className="font-display text-lg font-bold text-primary">Rename Horse</p>
            <p className="mt-1 text-xs text-muted-foreground">Renaming costs <b>{RENAME_COST.toLocaleString()} coins</b>.</p>
            <input data-testid="rename-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={24} autoFocus
              className="mt-3 h-11 w-full rounded-xl border border-border bg-background px-3 text-sm" placeholder="New name" />
            <div className="mt-4 flex gap-2">
              <button data-testid="rename-cancel" onClick={() => setOpen(false)} className="h-11 flex-1 rounded-xl border border-border font-bold text-primary active:scale-95">Cancel</button>
              <button data-testid="rename-confirm" disabled={busy || !name.trim()} onClick={submit} className="h-11 flex-1 rounded-xl bg-primary font-bold text-primary-foreground disabled:opacity-50 active:scale-95">{busy ? "…" : `Rename (${RENAME_COST}🪙)`}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};


// Copy/share a public link to a horse's profile page.
export const ShareControl = ({ playerNumber, aid, name }) => {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/game/horse/${playerNumber}/${aid}`;
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: `${name} · 120 Days to Makeover`, url }); return; } catch { /* user cancelled or unsupported — fall back to copy */ }
    }
    try { await navigator.clipboard.writeText(url); setCopied(true); toast.success("Profile link copied!"); setTimeout(() => setCopied(false), 2000); }
    catch { toast.error("Could not copy the link."); }
  };
  return (
    <button data-testid="share-horse-btn" onClick={share} aria-label="Share this horse's profile"
      className="ml-auto flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-sm font-semibold text-secondary active:scale-95">
      <Share2 size={15} /> {copied ? "Copied" : "Share"}
    </button>
  );
};

const fmtHeight = (ht) => { const s = String(ht || ""); return /hh$/i.test(s) ? s.replace(/\s*hh$/i, " hh") : (s ? `${s} hh` : ""); };
const headerInfoLine = (h) => {
  const parts = [];
  if (h.tag) parts.push(`Tag #${h.tag}`);
  if (h.hma) parts.push(`${h.hma} HMA`);
  if (h.sex) parts.push(h.sex);
  if (h.age != null && h.age !== "") parts.push(`${h.age} yr`);
  const ht = fmtHeight(h.height); if (ht) parts.push(ht);
  return parts.join(" · ");
};

// Permanent large-horse header used across all individual horse profile pages.
export const HorseProfileHeader = ({ horse, renameEndpoint, onRenamed, subLine, badge, bgOverride, actions }) => {
  const h = horse;
  const bg = bgOverride !== undefined ? bgOverride : h.background_image;
  const line1 = headerInfoLine(h);
  const line2 = [h.color, h.markings].filter(Boolean).join(" · ");
  const [showHma, setShowHma] = useState(false);
  return (
    <div data-testid="horse-profile-header">
      <div className="relative mx-4 mt-2 overflow-hidden rounded-3xl border border-border shadow-sm"
        style={bg ? undefined : { background: `radial-gradient(120% 92% at 50% 22%, ${h.color_hex}2e 0%, ${h.color_hex}0f 48%, transparent 100%)` }}>
        {bg && <img src={bg} alt="" className="absolute inset-0 h-full w-full object-cover" data-testid="horse-bg-layer" />}
        <div className="relative flex items-center justify-center px-4 py-7" style={{ minHeight: 240 }} data-testid="horse-hero-image">
          <HorseArt src={h.art_image} color={h.color_hex} size={220} />
        </div>
      </div>
      <div className="mx-4 mt-3">
        <div className="flex items-center gap-1.5">
          <h2 className="font-display text-3xl font-bold leading-tight text-primary" data-testid="horse-header-name">{h.name}</h2>
          {renameEndpoint && <RenameControl endpoint={renameEndpoint} currentName={h.name} onRenamed={onRenamed} />}
        </div>
        {line1 && <p className="mt-1 text-sm text-secondary" data-testid="horse-header-line1">{line1}</p>}
        {h.hma_origin_id && (
          <button data-testid="horse-hma-origin-link" onClick={() => setShowHma(true)}
            className="mt-0.5 block text-sm font-bold text-primary underline underline-offset-2 active:scale-95">
            HMA of Origin: {h.hma_origin_name || h.hma} →
          </button>
        )}
        {line2 && <p className="text-sm text-muted-foreground" data-testid="horse-header-line2">{line2}</p>}
        {subLine}
        {badge}
        {actions}
      </div>
      {showHma && h.hma_origin_id && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 p-4" onClick={() => setShowHma(false)}>
          <div className="max-h-[85vh] w-full max-w-md overflow-y-auto rounded-2xl border border-border bg-card p-4" onClick={(e) => e.stopPropagation()} data-testid="horse-hma-public-modal">
            <PublicProfile hmaId={h.hma_origin_id} />
            <button onClick={() => setShowHma(false)} className="mt-3 w-full rounded-lg bg-muted py-2 text-xs font-bold">Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export const HorseProfile = ({ save, onBack, catalog, shop, inventory, busy, onTrain, onEndDay, onCare, onOpenShop, onSkip, initialTab, onRenamed }) => {
  const h = save.horse;
  const [tab, setTab] = useState(initialTab || "care");
  const [bgImg, setBgImg] = useState(undefined);
  const careerHorse = { ...h, learned_skills: save.learned_skills || [], discipline: null, decision: null };
  return (
    <div className="pb-8" data-testid="horse-profile-view">
      <div className="flex items-center px-4 pt-4">
        <button data-testid="view-back" onClick={onBack} className="flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-sm font-semibold text-primary"><ChevronLeft size={15} /> Back</button>
      </div>
      <HorseProfileHeader horse={h} renameEndpoint="/game/makeover/rename" onRenamed={onRenamed}
        bgOverride={bgImg}
        actions={<BackgroundButton horseId={h.id} currentImage={bgImg !== undefined ? bgImg : h.background_image} onApply={setBgImg} />}
        subLine={<p className="mt-1.5 inline-block rounded-full bg-secondary/10 px-2.5 py-0.5 text-xs font-bold text-secondary" data-testid="makeover-day-badge">Day {save.day || 1} / 120</p>} />
      <div className="mx-4 mt-4 flex gap-1 overflow-x-auto" data-testid="makeover-profile-tabs">
        {[["care", "Care"], ["training", "Training"], ["stats", "Natural Stats"], ["discipline", "Discipline"], ["genetics", "Genetics"], ["tack", "Tack"], ["journal", "Journal"]].map(([k, l]) => (
          <button key={k} data-testid={`makeover-tab-${k}`} onClick={() => setTab(k)}
            className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-bold ${tab === k ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground"}`}>{l}</button>
        ))}
      </div>
      {tab === "care" && <div className="px-4 pt-3"><DailyCare save={save} shop={shop} inventory={inventory} onCare={onCare} onOpenShop={onOpenShop} busy={busy} /></div>}
      {tab === "training" && <TrainingDay save={save} catalog={catalog} onTrain={onTrain} onEndDay={onEndDay} onCare={onCare} onOpenShop={onOpenShop} onOpenJournal={() => setTab("journal")} onOpenProfile={() => setTab("care")} onSkip={onSkip || (() => {})} hideCare onGoCare={() => setTab("care")} shop={shop} inventory={inventory} busy={busy} />}
      {tab === "stats" && <NaturalStatsPanel a={careerHorse} />}
      {tab === "discipline" && <DisciplinePanel a={careerHorse} />}
      {tab === "genetics" && <HorseGenetics modelId={h.horse_art_record_id || h.art_id} />}
      {tab === "tack" && <MakeoverTackTab h={h} />}
      {tab === "journal" && <GameJournal save={save} onBack={() => setTab("care")} />}
    </div>
  );
};

const TACK_INV_NAMES = { western_saddle: "Western Saddle", western_bridle: "Western Bridle", western_pad: "Western Saddle Pad", english_saddle: "English Saddle", english_bridle: "English Bridle", english_pad: "English Saddle Pad", driving_harness: "Driving Harness", driving_cart: "Driving Cart", driving_whip: "Driving Whip" };
const TACK_INV_QLABEL = { basic: "Basic", good: "Good", supreme: "Supreme" };

export const GameInventory = ({ shop, inventory, player, initialTab = "all", onUse, onShopFeed, onShopTack, onBack }) => {
  const [cat, setCat] = useState(initialTab);
  const [bg, setBg] = useState(null);
  const loadBg = () => gameApi.get("/game/backgrounds/catalog").then((r) => setBg(r.data)).catch(() => setBg({ shop: [], owned: [], currency: 0 }));
  useEffect(() => { loadBg(); }, []);
  const items = Object.entries(shop || {}).flatMap(([c, arr]) => arr.map((it) => ({ ...it, cat: c }))).filter((it) => (inventory?.[it.id] || 0) > 0);
  const cats = ["all", "feeds", "supplements", "treats", "tack", "grooms", "cosmetics"];
  const shown = cat === "all" || cat === "tack" || cat === "grooms" ? items : items.filter((it) => it.cat === cat);
  const tack = player?.perf_tack || [];
  const nameById = Object.fromEntries((player?.alumni || []).filter((a) => a.decision === "keep").map((a) => [a.id, a.name]));
  const grooms = player?.grooms_available || 0;
  const showConsumables = cat === "all" || cat === "feeds" || cat === "supplements" || cat === "treats";
  const showTack = cat === "all" || cat === "tack";
  const showGrooms = cat === "all" || cat === "grooms";
  const showCosmetics = cat === "all" || cat === "cosmetics";
  return (
    <div className="pb-8" data-testid="inventory-view">
      <Back onBack={onBack} title="Inventory" year={player?.game_year} />
      <div className="mt-3 flex gap-2 overflow-x-auto px-4 pb-1">
        {cats.map((c) => <button key={c} data-testid={`inv-filter-${c}`} onClick={() => setCat(c)} className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-bold capitalize ${cat === c ? "border-secondary bg-secondary text-white" : "border-border bg-card text-muted-foreground"}`}>{c}</button>)}
      </div>
      <div className="space-y-2 px-4 pt-2">
        {showConsumables && shown.length === 0 && (cat !== "all" || tack.length === 0) && grooms === 0 && <p className="rounded-xl border border-dashed border-border p-4 text-center text-sm text-muted-foreground">No consumables yet — tap "Buy Feed, Supplements &amp; Treats" below to stock up.</p>}
        {showConsumables && shown.map((it) => (
          <div key={it.id} data-testid={`inv-item-${it.id}`} className="flex items-center justify-between rounded-xl border border-border bg-card p-3">
            <div>
              <p className="font-bold text-primary">{it.name}</p>
              <p className="text-[11px] text-muted-foreground">{Object.entries(it.eff || {}).map(([k, v]) => `${k} +${v}`).join(" · ") || "Treat"} · consumable</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-muted px-2.5 py-1 text-sm font-bold text-primary">×{inventory[it.id]}</span>
              {onUse && <button data-testid={`inv-use-${it.id}`} onClick={() => onUse(it.id, it.cat)} className="rounded-full border border-secondary px-3 py-1 text-xs font-bold text-secondary active:scale-95">Use Today</button>}
            </div>
          </div>
        ))}
        {showConsumables && onShopFeed && (
          <button data-testid="inv-shop-feed" onClick={onShopFeed} className="mt-1 flex w-full items-center justify-center gap-1.5 rounded-xl border border-secondary/40 bg-secondary/10 py-2.5 text-sm font-bold text-secondary active:scale-95"><ShoppingBag size={15} /> Buy Feed, Supplements &amp; Treats</button>
        )}
        {showTack && (
          <div className="pt-2" data-testid="inv-tack-section">
            <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-secondary">Performance Tack</p>
            {tack.length === 0 && <p className="rounded-xl border border-dashed border-border p-4 text-center text-sm text-muted-foreground">No tack yet.</p>}
            <div className="space-y-2">
              {tack.map((it) => {
                const equipped = it.equipped_by && nameById[it.equipped_by];
                return (
                  <div key={it.id} data-testid={`inv-tack-${it.id}`} className="flex items-center justify-between rounded-xl border border-border bg-card p-3">
                    <div className="min-w-0">
                      <p className="truncate font-bold text-primary">{TACK_INV_QLABEL[it.quality]} {TACK_INV_NAMES[it.key] || it.key}</p>
                      {equipped
                        ? <p className="text-[11px] font-semibold text-emerald-600">Equipped on {equipped}</p>
                        : <p className="text-[11px] text-muted-foreground">Not equipped</p>}
                    </div>
                    {equipped
                      ? <span className="shrink-0 rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-bold text-emerald-700">Equipped</span>
                      : <span className="shrink-0 rounded-full bg-muted px-2.5 py-1 text-[11px] font-bold text-muted-foreground">Idle</span>}
                  </div>
                );
              })}
            </div>
            {onShopTack && <button data-testid="inv-shop-tack" onClick={onShopTack} className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-xl border border-secondary/40 bg-secondary/10 py-2.5 text-sm font-bold text-secondary active:scale-95"><ShoppingBag size={15} /> Buy Performance Tack</button>}
          </div>
        )}
        {showGrooms && (
          <div className="pt-2" data-testid="inv-groom-section">
            <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-secondary">Groom Services</p>
            <div className="flex items-center justify-between rounded-xl border border-border bg-card p-3" data-testid="inv-grooms">
              <div>
                <p className="font-bold text-primary">Unused Groom Services</p>
                <p className="text-[11px] text-muted-foreground">Assign one to a horse from its Tack tab — auto-cares for 7 real days.</p>
              </div>
              <span className="rounded-full bg-muted px-2.5 py-1 text-sm font-bold text-primary" data-testid="inv-grooms-count">×{grooms}</span>
            </div>
          </div>
        )}
        {showCosmetics && bg && (
          <div className="pt-2" data-testid="inv-cosmetics-section">
            <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-secondary">Cosmetic Backgrounds</p>
            {(bg.owned || []).length === 0 && <p className="rounded-xl border border-dashed border-border p-3 text-center text-xs text-muted-foreground">No backgrounds owned yet — buy one in the Tack Shop or Barn Shop.</p>}
            <div className="space-y-2">
              {(bg.owned || []).map((o) => (
                <div key={o.bg_id} data-testid={`inv-bg-${o.bg_id}`} className="flex items-center gap-3 rounded-xl border border-border bg-card p-2">
                  {o.image_path ? <img src={o.image_path} alt="" className="h-11 w-16 shrink-0 rounded-lg object-cover" /> : <div className="grid h-11 w-16 shrink-0 place-items-center rounded-lg border border-dashed border-border text-[9px] text-muted-foreground">No art</div>}
                  <div className="min-w-0 flex-1"><p className="truncate text-sm font-bold text-primary">{o.name}</p><p className="text-[11px] text-muted-foreground">Owned {o.owned} · Equipped {o.equipped} · Available {o.available}</p></div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const GameJournal = ({ save, onBack }) => {
  const [f, setF] = useState("All");
  const KIND = { All: null, Training: "train", Care: "care", "Skills Learned": "skill", "Random Events": "event", Traits: "trait" };
  const entries = [...(save?.log || [])].reverse().filter((e) => !KIND[f] || e.kind === KIND[f]);
  return (
    <div className="pb-8" data-testid="journal-view">
      <Back onBack={onBack} title="Journal" />
      <div className="mt-3 flex gap-2 overflow-x-auto px-4 pb-1">
        {Object.keys(KIND).map((k) => <button key={k} data-testid={`journal-filter-${k}`} onClick={() => setF(k)} className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-bold ${f === k ? "border-secondary bg-secondary text-white" : "border-border bg-card text-muted-foreground"}`}>{k}</button>)}
      </div>
      <div className="space-y-1.5 px-4 pt-2">
        {entries.length === 0 && <p className="text-center text-sm text-muted-foreground">No entries for this filter.</p>}
        {entries.map((e, i) => <div key={i} className="rounded-lg border border-border bg-card px-3 py-2 text-xs text-foreground/80"><span className="font-bold text-secondary">Day {e.day}:</span> {e.text}</div>)}
      </div>
    </div>
  );
};

export const PremiumTeaser = ({ onOpen }) => {
  const [item, setItem] = useState(null);
  useEffect(() => {
    gameApi.get("/game/premium/items")
      .then((r) => setItem((r.data.items || []).find((i) => !i.owned) || null))
      .catch(() => {});
  }, []);
  if (!item) return null;
  const art = item.artwork ? (item.artwork.startsWith("http") ? item.artwork : `${process.env.REACT_APP_BACKEND_URL}${item.artwork}`) : null;
  return (
    <button data-testid="premium-teaser" onClick={onOpen}
      className="mt-3 flex w-full items-center gap-3 overflow-hidden rounded-2xl border border-fuchsia-300/40 bg-gradient-to-r from-fuchsia-600 to-indigo-600 p-3 text-left text-white shadow-sm transition-transform active:scale-[0.99]">
      <div className="grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-xl bg-white/15">
        {art ? <img src={art} alt="" className="h-full w-full object-cover" /> : <Gem size={22} />}
      </div>
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-fuchsia-100"><Sparkles size={12} /> New in the Premium Shop</p>
        <p className="truncate font-display text-base font-bold">{item.name}</p>
        <p className="truncate text-xs text-white/80">{item.price_display}{item.coats ? ` · ${item.coats.length} exclusive coats` : ""}</p>
      </div>
      <ArrowRight size={18} className="shrink-0" />
    </button>
  );
};

export const GameHome = ({ player, save, onNavigate }) => {
  const h = save?.horse;
  const active = save && save.status !== "selecting";
  return (
    <div className="pb-8 px-4 pt-4" data-testid="game-home-view">
      <div className="rounded-2xl border border-border bg-card p-4">
        <p className="font-display text-2xl font-bold text-primary">Welcome back, {player?.display_name}!</p>
        <p className="mt-1 flex items-center gap-1 text-sm font-bold text-amber-700"><Coins size={15} /> {player?.currency} coins</p>
      </div>
      <PremiumTeaser onOpen={() => onNavigate("premium")} />
      {active && h ? (
        <div className="mt-3 rounded-2xl border border-border bg-card p-4">
          <div className="flex items-center gap-3">
            <div className="grid h-16 w-16 shrink-0 place-items-center rounded-xl" style={{ background: `${h.color_hex}1a` }}><HorseArt src={h.art_image} color={h.color_hex} size={60} /></div>
            <div>
              <p className="font-display text-xl font-bold text-primary">{h.name}</p>
              <p className="text-xs font-semibold text-secondary">Tag #{h.tag} · Day {save.day} of 120</p>
              <div className="mt-1"><ReadinessPill readiness={save.readiness} /></div>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <button data-testid="home-continue" onClick={() => onNavigate("play")} className="flex items-center justify-center gap-2 rounded-xl bg-primary py-3 text-sm font-bold text-primary-foreground active:scale-95"><Dumbbell size={15} /> Continue Training</button>
            <button data-testid="home-profile" onClick={() => onNavigate("profile")} className="flex items-center justify-center gap-2 rounded-xl border border-border py-3 text-sm font-bold text-primary active:scale-95"><User size={15} /> Horse Profile</button>
            <button data-testid="home-feed" onClick={() => onNavigate("feedroom")} className="flex items-center justify-center gap-2 rounded-xl border border-border py-3 text-sm font-bold text-primary active:scale-95"><Utensils size={15} /> Feed Room</button>
            <button data-testid="home-stable" onClick={() => onNavigate("stable")} className="flex items-center justify-center gap-2 rounded-xl border border-border py-3 text-sm font-bold text-primary active:scale-95"><Warehouse size={15} /> My Stable</button>
          </div>
        </div>
      ) : (
        <p className="mt-3 rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">No makeover in progress. Start a new one from the training screen.</p>
      )}
    </div>
  );
};

const placeSuffix = (n) => (n === 1 ? "st" : n === 2 ? "nd" : n === 3 ? "rd" : "th");
const scoreOf = (a, key) => (a.scores ? a.scores[key] : a[key]);

export const MyStable = ({ player, save, onBack, onNewMakeover, onOpen, onOpenCurrent, onCollect, onEnter, onSell, onOpenStall, onOpenTack, onOpenFeed, onAlumni, onGameHome, onUpgrades }) => {
  const kept = (player?.alumni || []).filter((a) => a.decision === "keep");
  const current = save?.status === "training" && save.horse ? save.horse : null;
  return (
    <div className="pb-8" data-testid="stable-view">
      <Back onBack={onBack} title="My Stable" />
      <div className="px-4 pt-3">
        <BarnHome player={player} current={current} onOpenCurrent={onOpenCurrent} onOpenStall={onOpenStall} onOpenTack={onOpenTack} onOpenFeed={onOpenFeed}
          onNewMakeover={onNewMakeover} onCollect={onCollect} onAlumni={onAlumni} onGameHome={onGameHome} onUpgrades={onUpgrades} />
        {current && (
          <button data-testid="stable-current-horse" onClick={() => onOpenCurrent && onOpenCurrent()}
            className="mb-3 flex w-full items-center gap-3 rounded-2xl border-2 border-secondary/50 bg-secondary/5 p-3 text-left active:scale-[0.99]">
            <div className="grid h-14 w-14 shrink-0 place-items-center rounded-xl" style={{ background: `${current.color_hex}1a` }}><HorseArt src={current.art_image} color={current.color_hex} size={52} /></div>
            <div className="flex-1 text-sm">
              <p className="font-display text-lg font-bold text-primary">{current.name}</p>
              <p className="text-xs font-semibold text-secondary">In Makeover · Day {save.day || 1} / 120</p>
              <p className="text-[11px] text-muted-foreground">Tap for natural stats, discipline aptitude &amp; training</p>
            </div>
            <span className="shrink-0 rounded-full bg-secondary px-2 py-0.5 text-[10px] font-bold text-white">Active</span>
          </button>
        )}
        {kept.length === 0 ? (
          <p className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">{current ? "Your current makeover horse is above. Kept horses appear here after you finish a makeover and choose “Keep.”" : "No horses kept yet. Finish a makeover and choose “Keep” to add them to your stable."}</p>
        ) : (
          <div className="space-y-3">
            {kept.map((a, i) => (
              <div key={a.id || i} data-testid={`stable-horse-${i}`} className="rounded-2xl border border-border bg-card p-3">
                <button data-testid={`stable-horse-open-${i}`} onClick={() => onOpen && onOpen(a)} className="flex w-full items-center gap-3 text-left active:scale-[0.99]">
                  <div className="grid h-14 w-14 shrink-0 place-items-center rounded-xl" style={{ background: `${a.color_hex}1a` }}><HorseArt src={a.art_image} color={a.color_hex} size={52} /></div>
                  <div className="text-sm">
                    <p className="font-display text-lg font-bold text-primary">{a.name}</p>
                    <p className="text-xs font-semibold text-secondary">Tag #{a.tag} · {a.hma} HMA</p>
                    <p className="text-xs text-muted-foreground">Placed {scoreOf(a, "placing")}{placeSuffix(scoreOf(a, "placing"))} · Overall {scoreOf(a, "overall")}</p>
                    {(a.stable_trainings > 0 || a.stable_wins > 0) && (
                      <p className="text-[11px] font-semibold text-green-700">🏡 {a.stable_trainings || 0} extra sessions · {a.stable_wins || 0} wins</p>
                    )}
                    {Object.keys(player?.cosmetics?.equipped?.[a.id] || {}).length > 0 && (
                      <p className="text-[11px] font-semibold text-secondary" data-testid={`stable-tack-${i}`}>🎽 {Object.keys(player.cosmetics.equipped[a.id]).length} tack equipped</p>
                    )}
                  </div>
                </button>
                {a.life_status === "deceased" ? (
                  <div className="mt-2">
                    <p data-testid={`stable-deceased-${i}`} className="mb-1.5 rounded-lg bg-secondary/10 px-2 py-1 text-center text-[11px] font-bold text-secondary">🕊️ Deceased — Age {a.death_age} · Game Year {a.death_game_year}</p>
                    <button data-testid={`stable-memorial-${i}`} onClick={() => onOpen && onOpen(a)} className="h-10 w-full rounded-xl border-2 border-border text-xs font-bold text-primary active:scale-95">View Memorial</button>
                  </div>
                ) : (
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <button data-testid={`stable-open-btn-${i}`} onClick={() => onOpen && onOpen(a)}
                      className="h-10 rounded-xl bg-primary text-xs font-bold text-primary-foreground active:scale-95">Open · Train &amp; Care</button>
                    <button data-testid={`stable-sell-${i}`} onClick={() => onSell && onSell(a)}
                      className="h-10 rounded-xl border-2 border-amber-500 text-xs font-bold text-amber-600 active:scale-95">Sell / Rehome</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export const AlumniBook = ({ onBack, onOpen }) => {
  const [data, setData] = useState(null);
  useEffect(() => { gameApi.get("/game/alumni").then(({ data }) => setData(data)).catch(() => setData({ alumni: [], unread_updates: 0 })); }, []);
  const all = data?.alumni || [];
  return (
    <div className="pb-8" data-testid="alumni-view">
      <Back onBack={onBack} title="Alumni Book" />
      <div className="px-4 pt-3">
        {data === null ? <p className="text-center text-sm text-muted-foreground">Loading…</p> : all.length === 0 ? (
          <p className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">No finished horses yet. Complete a 120-day makeover to fill your Alumni Book.</p>
        ) : (
          <div className="space-y-2">
            {all.map((a, i) => {
              const unread = (a.updates || []).some((u) => !u.read);
              return (
                <button key={a.id || i} data-testid={`alumni-${i}`} onClick={() => onOpen && onOpen(a)}
                  className="flex w-full items-center gap-3 rounded-2xl border border-border bg-card p-3 text-left active:scale-[0.99]">
                  <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl" style={{ background: `${a.color_hex}1a` }}><HorseArt src={a.art_image} color={a.color_hex} size={44} /></div>
                  <div className="flex-1 text-sm">
                    <p className="flex items-center gap-1.5 font-bold text-primary">{a.name}
                      {unread && <span data-testid={`alumni-unread-${i}`} className="rounded-full bg-secondary px-1.5 py-0.5 text-[9px] font-bold text-white">NEW</span>}</p>
                    <p className="text-xs text-muted-foreground">Placed {scoreOf(a, "placing")}{placeSuffix(scoreOf(a, "placing"))} · Overall {scoreOf(a, "overall")} · {a.decision === "sell" ? `Sold ${a.sale_price}🪙` : "Kept"}</p>
                    <p className="text-[11px] text-muted-foreground">{a.stable_name}</p>
                  </div>
                  <ChevronLeft size={16} className="rotate-180 text-muted-foreground" />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const AlSection = ({ title, children }) => (
  <div className="mx-4 mt-3 rounded-2xl border border-border bg-card p-4">
    <p className="text-xs font-bold uppercase tracking-wider text-secondary">{title}</p>
    <div className="mt-2">{children}</div>
  </div>
);

export const DisciplineCompetitions = ({ player, onBack, onOpen }) => {
  const career = (player?.alumni || []).filter((a) => a.decision === "keep");
  const chosen = career.filter((a) => a.discipline);
  const readyToChoose = career.filter((a) => !a.discipline && isEligible(a));
  const inMakeover = career.filter((a) => !a.discipline && !isEligible(a));
  const Row = ({ a, tag, tagClass }) => (
    <button data-testid={`disc-comp-horse-${a.id}`} onClick={() => onOpen && onOpen(a)}
      className="flex w-full items-center gap-3 rounded-2xl border border-border bg-card p-3 text-left active:scale-[0.99]">
      <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl" style={{ background: `${a.color_hex}1a` }}><HorseArt src={a.art_image} color={a.color_hex} size={44} /></div>
      <div className="min-w-0 flex-1">
        <p className="truncate font-bold text-primary">{a.name}</p>
        {a.discipline
          ? <p className="text-xs text-secondary">{a.discipline} · {a.career?.level || "Novice"} · {a.career?.wins || 0}W / {a.career?.starts || 0} starts</p>
          : <p className="text-xs text-muted-foreground">Tag #{a.tag} · {a.hma} HMA</p>}
      </div>
      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold ${tagClass}`}>{tag}</span>
    </button>
  );
  return (
    <div className="pb-10" data-testid="discipline-competitions-view">
      <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-border bg-card/95 px-4 py-3 backdrop-blur">
        <button data-testid="disc-comp-back" onClick={onBack} className="rounded-full p-1 text-muted-foreground"><ChevronLeft size={20} /></button>
        <Flag className="text-amber-500" size={20} /><h2 className="font-display text-xl font-bold text-primary">Discipline Competitions</h2>
      </div>
      <div className="px-4 pt-3 space-y-4">
        <div data-testid="live-shows-section">
          <p className="mb-2 text-xs font-bold uppercase tracking-wider text-secondary">Live &amp; Upcoming Shows <span className="font-normal text-muted-foreground">· tap to view entries &amp; scores</span></p>
          <LiveShowsBoard player={player} />
        </div>
        <div>
        <p className="mb-2 text-xs font-bold uppercase tracking-wider text-secondary">Your Horses</p>
        {career.length === 0 ? (
          <p className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">Keep a horse and complete its 120-day makeover to start a competition career.</p>
        ) : (
          <div className="space-y-4">
            {chosen.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-bold uppercase tracking-wider text-secondary">Your Competitors</p>
                {chosen.map((a) => <Row key={a.id} a={a} tag="Compete" tagClass="bg-emerald-100 text-emerald-700" />)}
              </div>
            )}
            {readyToChoose.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-bold uppercase tracking-wider text-secondary">Ready to Choose a Discipline</p>
                {readyToChoose.map((a) => <Row key={a.id} a={a} tag="Choose" tagClass="bg-amber-100 text-amber-700" />)}
              </div>
            )}
            {inMakeover.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-bold uppercase tracking-wider text-secondary">Still in Training</p>
                {inMakeover.map((a) => <Row key={a.id} a={a} tag="Locked" tagClass="bg-muted text-muted-foreground" />)}
              </div>
            )}
            <p className="text-center text-[11px] text-muted-foreground">Tap a horse to view its profile, choose a discipline, train, and enter shows.</p>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export const AlumniProfile = ({ alumni, keptHorses = [], playerNumber, onBack, save, catalog, shop, inventory, busy, onTrain, onEndDay, onCare, onOpenShop, onEnsureStable, onStableCompete, onSaveUpdate, onRenamed }) => {
  const a = alumni;
  const deceased = a?.life_status === "deceased";
  const kept = a?.decision === "keep" && !deceased;
  const [tab, setTab] = useState("care");
  const [bgImg, setBgImg] = useState(undefined);
  useEffect(() => { if (a?.id) gameApi.post(`/game/alumni/${a.id}/read`).catch(() => {}); }, [a?.id]);
  if (!a) return null;
  const fs = a.final_stats || {};
  const sc = a.scores || {};
  const STAT_ROWS = ["trust", "handling", "manners", "confidence", "conditioning", "trail", "freestyle", "riding", "liberty", "popularity", "health"];
  return (
    <div className="pb-10" data-testid="alumni-profile" aria-label={`Alumni profile for ${a.name}`}>
      <div className="flex items-center gap-2 px-4 pt-4">
        <button data-testid="view-back" onClick={onBack} className="flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-sm font-semibold text-primary"><ChevronLeft size={15} /> Back</button>
        {kept && playerNumber != null && <ShareControl playerNumber={playerNumber} aid={a.id} name={a.name} />}
      </div>
      <HorseProfileHeader horse={a} renameEndpoint={`/game/alumni/${a.id}/rename`} onRenamed={onRenamed}
        bgOverride={bgImg}
        actions={deceased ? null : <BackgroundButton horseId={a.id} currentImage={bgImg !== undefined ? bgImg : a.background_image} onApply={setBgImg} />}
        badge={deceased
          ? <p className="mt-1.5 inline-block rounded-full bg-secondary/15 px-2.5 py-0.5 text-xs font-bold text-secondary" data-testid="deceased-badge">🕊️ Deceased — Age {a.death_age} · Passed in Game Year {a.death_game_year} from Old Age</p>
          : (a.discipline ? <p className="mt-1.5 inline-block rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-bold text-emerald-700" data-testid="career-badge">Career: {a.discipline}</p> : null)} />

      {kept && (
        <div className="mx-4 mt-4 flex gap-1 overflow-x-auto" data-testid="horse-profile-tabs">
          {[["care", "Care"], ["train", "Training"], ["stats", "Natural Stats"], ["discipline", "Discipline"], ["genetics", "Genetics"], ["tack", "Tack"], ["journal", "Journal"], ...(a.discipline ? [["comp", "Competition"]] : []), ["awards", "Awards & Titles"]].map(([k, l]) => (
            <button key={k} data-testid={`horse-tab-${k}`} onClick={() => setTab(k)}
              className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-bold ${tab === k ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground"}`}>{l}</button>
          ))}
        </div>
      )}

      {kept && tab === "stats" && <NaturalStatsPanel a={a} />}
      {kept && tab === "discipline" && <DisciplinePanel a={a} />}
      {kept && tab === "genetics" && <HorseGenetics modelId={a.horse_art_record_id || a.art_id} />}
      {kept && tab === "tack" && <HorseTackTab a={a} keptHorses={keptHorses} />}
      {kept && tab === "comp" && <CompetitionPanel a={a} />}
      {kept && tab === "awards" && <AwardsPanel a={a} />}
      {kept && (() => { const stableActive = save?.status === "stable" && save?.stable_alumni_id === a.id; return (<>
        {tab === "train" && (!a.discipline
          ? (!isEligible(a)
              ? (stableActive
                  ? <TrainingDay save={save} catalog={catalog} onTrain={onTrain} onEndDay={onEndDay} onCare={onCare} onOpenShop={onOpenShop} onOpenJournal={() => setTab("journal")} onOpenProfile={() => setTab("care")} onSkip={() => {}} hideCare onGoCare={() => setTab("care")} shop={shop} inventory={inventory} busy={busy} />
                  : <div className="mx-4 mt-3 rounded-2xl border border-border bg-card p-5 text-center" data-testid="train-start-foundation"><p className="text-sm text-muted-foreground">{a.name} still has foundation training to finish before choosing a competition discipline.</p><button data-testid="profile-start-foundation" disabled={busy} onClick={() => onEnsureStable && onEnsureStable(a)} className="mt-3 w-full rounded-xl bg-primary py-2.5 text-sm font-bold text-primary-foreground disabled:opacity-60">Start a training session</button></div>)
              : <div className="mx-4 mt-3 rounded-2xl border border-border bg-card p-5 text-center" data-testid="train-need-discipline"><p className="text-sm text-muted-foreground">{a.name} has finished its foundation training! Open the <b>Discipline</b> tab to choose a competition career.</p></div>)
          : stableActive
            ? <AdvancedTrainingPanel a={a} save={save} busy={busy} onSaveUpdate={onSaveUpdate} onEndDay={onEndDay} onGoCare={() => setTab("care")} onCompete={() => setTab("comp")} />
            : <div className="mx-4 mt-3 rounded-2xl border border-border bg-card p-5 text-center" data-testid="train-start"><p className="text-sm text-muted-foreground">Give {a.name} advanced {a.discipline} training — feed, train and build skill bars.</p><button data-testid="profile-start-training" disabled={busy} onClick={() => onEnsureStable && onEnsureStable(a)} className="mt-3 w-full rounded-xl bg-primary py-2.5 text-sm font-bold text-primary-foreground disabled:opacity-60">Start a training session</button></div>)}
        {tab === "care" && (stableActive
          ? <div className="px-4 pt-3"><DailyCare save={save} shop={shop} inventory={inventory} onCare={onCare} onOpenShop={onOpenShop} busy={busy} /></div>
          : <div className="mx-4 mt-3 rounded-2xl border border-border bg-card p-5 text-center" data-testid="care-start"><p className="text-sm text-muted-foreground">Start a session to feed &amp; water {a.name}.</p><button data-testid="profile-start-care" disabled={busy} onClick={() => onEnsureStable && onEnsureStable(a)} className="mt-3 w-full rounded-xl bg-primary py-2.5 text-sm font-bold text-primary-foreground disabled:opacity-60">Start a session</button></div>)}
        {tab === "journal" && (<div className="mx-4 mt-3 space-y-3" data-testid="alumni-journal">
          <AlSection title="Updates From Their New Home">{(a.updates || []).length ? a.updates.map((u, i) => <div key={i} className="rounded-xl bg-muted/50 p-2.5"><p className="text-[10px] font-bold uppercase text-secondary">{u.label}</p><p className="text-xs text-foreground/85">{u.text}</p></div>) : <p className="text-xs text-muted-foreground">No journal entries yet.</p>}</AlSection>
          <AlSection title="Skills & Training History"><div className="flex flex-wrap gap-1.5">{(a.learned_skills || []).map((s) => <span key={s} className="rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-semibold text-green-700">{s}</span>)}</div></AlSection>
        </div>)}
      </>); })()}

      {!kept && (<>
      {(a.updates || []).length > 0 && (
        <AlSection title="Updates From Their New Home">
          <div className="space-y-2" data-testid="alumni-updates">
            {a.updates.map((u, i) => (
              <div key={i} className="rounded-xl bg-muted/50 p-2.5">
                <p className="text-[10px] font-bold uppercase text-secondary">{u.label}</p>
                <p className="text-xs text-foreground/85">{u.text}</p>
              </div>
            ))}
          </div>
        </AlSection>
      )}

      <AlSection title="Overview">
        <p className="text-xs text-muted-foreground">New owner: <span className="font-semibold text-primary">{a.buyer}</span> · {a.stable_name}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{a.decision === "sell" ? `Sold for ${a.sale_price}🪙` : "Kept in your stable"} · {(a.date || "").slice(0, 10)}</p>
        <div className="mt-2 flex flex-wrap gap-1.5">{(a.personality || []).map((t) => <span key={t} className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold">{t}</span>)}
          {(a.discovered_traits || []).map((t) => <span key={t} className="rounded-full bg-secondary/15 px-2 py-0.5 text-[11px] font-semibold text-secondary">{t}</span>)}</div>
      </AlSection>

      {(a.stable_trainings > 0 || a.stable_wins > 0 || (a.stable_comp_history || []).length > 0) && (
        <AlSection title="Extra Training & Competitions">
          <p className="text-xs text-muted-foreground">Extra training sessions: <span className="font-semibold text-primary">{a.stable_trainings || 0}</span> · Competition wins: <span className="font-semibold text-primary">{a.stable_wins || 0}</span></p>
          {(a.stable_comp_history || []).length > 0 && (
            <div className="mt-1 space-y-0.5 text-[11px] text-muted-foreground" data-testid="alumni-stable-comps">
              {a.stable_comp_history.slice(-8).map((c, i) => <p key={i}>Day {c.day}: placed {c.placing} · overall {c.overall} · +{c.prize}🪙{c.win ? " · WIN 🏆" : ""}</p>)}
            </div>
          )}
        </AlSection>
      )}

      <AlSection title="Final Training Stats">
        <div className="grid grid-cols-2 gap-x-4 gap-y-2">{STAT_ROWS.map((k) => <StatBar key={k} label={k[0].toUpperCase() + k.slice(1)} value={fs[k] || 0} tone={k === "popularity" ? "secondary" : "primary"} />)}</div>
      </AlSection>

      <AlSection title="Skills & Training History">
        <p className="text-xs text-muted-foreground">Required completed: <span className="font-semibold text-primary">{(a.required_completed || []).length}</span> · Optional: <span className="font-semibold text-primary">{(a.optional_completed || []).length}</span></p>
        <p className="mt-0.5 text-xs text-muted-foreground">Manual days {a.history?.manual || 0} · Rest days {a.history?.rest || 0} · Skipped days {a.history?.skipped || 0}</p>
        {a.most_category && <p className="mt-0.5 text-xs text-muted-foreground">Most practiced: {a.most_category} · Least: {a.least_category}</p>}
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="alumni-skills">{(a.learned_skills || []).map((s) => <span key={s} className="rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-semibold text-green-700">{s}</span>)}</div>
      </AlSection>

      <AlSection title="Competition Results">
        <p className="text-sm font-bold text-primary" data-testid="alumni-placing">Placed {sc.placing}{placeSuffix(sc.placing)} of {sc.total_entries} · Overall {sc.overall}</p>
        <div className="mt-2 space-y-1.5 text-xs">
          {["handling", "trail", "freestyle"].map((c) => (
            <div key={c}>
              <p className="font-semibold text-primary capitalize">{c === "handling" ? "Handling & Conditioning" : c}: {sc[c]} {sc.bonuses && <span className="text-green-700">(+{sc.bonuses[c]} bonus)</span>}</p>
              {sc.comments && <p className="italic text-muted-foreground">“{sc.comments[c]}”</p>}
            </div>
          ))}
          {sc.penalty > 0 && <p className="text-amber-700">−{sc.penalty} readiness penalty</p>}
          <p className="text-muted-foreground">Judges: {(a.judges || []).join(" · ")}</p>
          <p className="text-muted-foreground">Fan Favorite: {a.fan_favorite || "Not held"}</p>
        </div>
      </AlSection>

      {a.auction && (
        <AlSection title="Auction Record">
          <p className="text-sm font-bold text-primary">Winning bid: {a.auction.hammer}🪙 — {a.buyer}</p>
          <div className="mt-1 space-y-0.5 text-xs text-muted-foreground" data-testid="alumni-auction-bids">
            {a.auction.bids.map((b, i) => <div key={i}>{b.bidder} — {b.amount}🪙{b.winning ? " · SOLD" : ""}</div>)}
          </div>
        </AlSection>
      )}

      {(a.journal || []).length > 0 && (
        <AlSection title="Journal Highlights">
          <div className="space-y-1 text-[11px] text-muted-foreground">{a.journal.slice(-12).map((e, i) => <p key={i}>Day {e.day}: {e.text}</p>)}</div>
        </AlSection>
      )}
      </>)}
    </div>
  );
};

export const AchievementsView = ({ onBack }) => {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");
  useEffect(() => {
    gameApi.get("/game/achievements").then(({ data }) => setItems(data.achievements)).catch(() => setItems([]));
  }, []);
  const unlockedCount = (items || []).filter((a) => a.unlocked).length;
  const shown = (items || []).filter((a) =>
    filter === "unlocked" ? a.unlocked
      : filter === "progress" ? (!a.unlocked && a.progress && a.progress.current > 0)
      : filter === "gold" ? a.tier === "gold"
      : true);
  const TIER = { gold: ["Gold", "border-amber-300 bg-amber-100 text-amber-700"], silver: ["Silver", "border-slate-300 bg-slate-100 text-slate-600"], bronze: ["Bronze", "border-orange-300 bg-orange-100 text-orange-700"] };
  return (
    <div className="pb-8" data-testid="achievements-view">
      <Back onBack={onBack} title="Achievements" />
      <div className="px-4 pt-3">
        {items === null ? (
          <p className="text-center text-sm text-muted-foreground">Loading…</p>
        ) : (
          <>
            <p className="mb-2 text-sm font-semibold text-secondary" data-testid="ach-progress">{unlockedCount} / {items.length} unlocked</p>
            <div className="mb-3 flex flex-wrap gap-1.5">
              {[["all", `All (${items.length})`], ["unlocked", `Unlocked (${unlockedCount})`], ["progress", "In progress"], ["gold", "Gold tier"]].map(([k, l]) => (
                <button key={k} data-testid={`ach-filter-${k}`} onClick={() => setFilter(k)}
                  className={`rounded-full border px-2.5 py-1 text-[11px] font-bold ${filter === k ? "border-secondary bg-secondary text-white" : "border-border bg-card text-muted-foreground"}`}>{l}</button>
              ))}
            </div>
            {shown.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground" data-testid="ach-empty">No achievements match this filter.</p>}
            <div className="grid grid-cols-2 gap-2">
              {shown.map((a) => {
                const Icon = ACH_ICONS[a.icon] || Trophy;
                const t = TIER[a.tier] || TIER.bronze;
                const pr = a.progress;
                return (
                  <div key={a.id} data-testid={`ach-${a.id}`}
                    className={`rounded-2xl border p-3 ${a.unlocked ? "border-secondary/40 bg-secondary/10" : "border-border bg-card opacity-80"}`}>
                    <div className="flex items-center gap-2">
                      <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-full ${a.unlocked ? "bg-secondary text-white" : "bg-muted text-muted-foreground"}`}>
                        {a.unlocked ? <Icon size={18} /> : <Lock size={16} />}
                      </span>
                      <p className="text-sm font-bold text-primary">{a.name}</p>
                    </div>
                    <p className="mt-1.5 text-[11px] leading-snug text-muted-foreground">{a.desc}</p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                      <span className={`rounded-full border px-1.5 py-0.5 text-[9px] font-bold ${t[1]}`} data-testid={`ach-tier-${a.id}`}>{t[0]} · {a.rarity_pct}%</span>
                      {a.coin_reward > 0 && <span className="rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[9px] font-bold text-amber-700">🪙 {a.coin_reward.toLocaleString()}</span>}
                    </div>
                    {!a.unlocked && pr && pr.target > 1 && (
                      <div className="mt-1.5" data-testid={`ach-bar-${a.id}`}>
                        <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-secondary" style={{ width: `${Math.min(100, (pr.current / pr.target) * 100)}%` }} /></div>
                        <p className="mt-0.5 text-[9px] text-muted-foreground">{pr.current.toLocaleString()} / {pr.target.toLocaleString()}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export const ComingSoon = ({ title, onBack }) => {
  const Icon = title === "My Stable" ? Warehouse : title === "Alumni Book" ? BookOpen : Trophy;
  return (
    <div className="pb-8" data-testid="coming-soon-view">
      <Back onBack={onBack} title={title} />
      <div className="mx-4 mt-6 rounded-2xl border border-dashed border-border bg-card/60 p-8 text-center">
        <Icon size={32} className="mx-auto text-secondary/50" />
        <p className="mt-3 font-display text-lg font-bold text-primary">Coming soon</p>
        <p className="mt-1 text-sm text-muted-foreground">{title} unlocks with the competition &amp; auction update, when you can keep or sell your finished horses.</p>
      </div>
    </div>
  );
};
